import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { DemoProvider } from "./DemoContext";

const navigation = [
    { name: "Dashboard", href: "/demo", icon: LayoutDashboard },
    { name: "Accounts", href: "/demo/accounts", icon: Wallet },
    { name: "Insights", href: "/demo/insights", icon: Lightbulb },
    { name: "Goals", href: "/demo/goals", icon: Target },
    { name: "Investments", href: "/demo/investments", icon: TrendingUp },
    { name: "Projections", href: "/demo/projections", icon: Calculator },
];

// Simple icon components (same as AppLayout)
function LayoutDashboard({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
    );
}

function Wallet({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
            <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
        </svg>
    );
}

function TrendingUp({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
        </svg>
    );
}

function Target({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
        </svg>
    );
}

function Calculator({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <line x1="8" y1="6" x2="16" y2="6" />
            <line x1="8" y1="10" x2="8" y2="10.01" />
            <line x1="12" y1="10" x2="12" y2="10.01" />
            <line x1="16" y1="10" x2="16" y2="10.01" />
            <line x1="8" y1="14" x2="8" y2="14.01" />
            <line x1="12" y1="14" x2="12" y2="14.01" />
            <line x1="16" y1="14" x2="16" y2="14.01" />
            <line x1="8" y1="18" x2="8" y2="18.01" />
            <line x1="12" y1="18" x2="16" y2="18" />
        </svg>
    );
}

function Lightbulb({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
        </svg>
    );
}

function Menu({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
    );
}

function X({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function SidebarLink({ item, onClick }) {
    return (
        <NavLink
            to={item.href}
            end={item.href === "/demo"}
            onClick={onClick}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                        ? "bg-accent-50 text-accent-600 border border-accent-200"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                }`
            }
        >
            <item.icon className="w-5 h-5" />
            <span>{item.name}</span>
        </NavLink>
    );
}

function Sidebar({ mobileOpen, setMobileOpen }) {
    const content = (
        <div className="flex flex-col h-full">
            {/* Logo */}
            <NavLink
                to="/"
                className="flex items-center gap-3 px-4 h-16 border-b border-gray-200 hover:bg-gray-50 transition-colors"
            >
                <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">O</span>
                </div>
                <span className="font-semibold text-gray-900">OmniWorth</span>
            </NavLink>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
                {navigation.map((item) => (
                    <SidebarLink key={item.name} item={item} onClick={() => setMobileOpen(false)} />
                ))}

                {/* CTA instead of Link Account */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                    <Link
                        to="/signup"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold btn-primary transition-all duration-200"
                    >
                        <span>Start Free Trial</span>
                    </Link>
                </div>
            </nav>

            {/* Demo User Info */}
            <div className="p-4 border-t border-gray-200">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">👋</span>
                        <span className="text-sm font-medium text-gray-900">Demo Mode</span>
                    </div>
                    <p className="text-xs text-gray-500">
                        Exploring with sample data. Sign up to connect your real accounts.
                    </p>
                </div>
                <Link
                    to="/"
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Back to Home</span>
                </Link>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform lg:hidden ${
                    mobileOpen ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <button
                    onClick={() => setMobileOpen(false)}
                    className="absolute top-4 right-4 p-1 text-gray-500 hover:text-gray-900"
                >
                    <X className="w-5 h-5" />
                </button>
                {content}
            </div>

            {/* Desktop sidebar */}
            <div className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-white border-r border-gray-200">
                {content}
            </div>
        </>
    );
}

function Header({ onMenuClick }) {
    return (
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-md border-b border-gray-200">
            <div className="flex items-center justify-between h-full px-4 lg:px-6">
                <button
                    onClick={onMenuClick}
                    className="lg:hidden p-2 text-gray-500 hover:text-gray-900"
                >
                    <Menu className="w-5 h-5" />
                </button>

                {/* Demo badge */}
                <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-accent-50 text-accent-600 text-xs font-medium rounded-full border border-accent-200">
                        Demo Mode
                    </span>
                </div>

                <div className="flex-1" />

                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 hidden sm:block">
                        {new Date().toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "short",
                            day: "numeric",
                        })}
                    </span>
                    <Link
                        to="/signup"
                        className="btn-primary px-4 py-1.5 text-sm"
                    >
                        Sign Up
                    </Link>
                </div>
            </div>
        </header>
    );
}

export default function DemoAppLayout({ children }) {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <DemoProvider>
            <div className="min-h-screen bg-gray-50">
                <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

                <div className="lg:pl-64">
                    <Header onMenuClick={() => setMobileOpen(true)} />
                    <main className="p-4 lg:p-8">
                        {children}
                    </main>
                </div>
            </div>
        </DemoProvider>
    );
}
